import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, BellRing, Play, Eye, Save, Search } from "lucide-react";
import { toast } from "sonner";

const CAT_LABELS = { licences: "Licences", certifications: "Certifications", inductions: "Inductions", contracts: "Contracts" };

export default function EscalationsPanel() {
  const [cfg, setCfg] = useState(null);
  const [mailbox, setMailbox] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);
  const [empQ, setEmpQ] = useState("");
  const [emps, setEmps] = useState([]);

  const load = async () => {
    try {
      const [{ data: c }, { data: l }] = await Promise.all([
        api.get("/escalations/config"), api.get("/escalations/log")]);
      setCfg(c); setMailbox(c.inductions_mailbox); setLog(l.rows);
    } catch (e) { toast.error(e.response?.data?.detail || "Escalations load failed"); }
  };
  useEffect(() => { load(); }, []);

  const setCat = (cat, patch) => setCfg({ ...cfg, categories: { ...cfg.categories, [cat]: { ...cfg.categories[cat], ...patch } } });

  const saveCat = async (cat) => {
    const c = cfg.categories[cat];
    try {
      await api.put(`/escalations/config/${cat}`, {
        enabled: c.enabled, periods: c.periods, daily_final: c.daily_final,
        to_employee: c.to_employee, to_manager: c.to_manager,
        to_inductions_mailbox: c.to_inductions_mailbox,
        extra_emails: (c.extra_raw !== undefined ? c.extra_raw.split(",") : c.extra_emails).map((e) => e.trim()).filter(Boolean),
      });
      toast.success(`${CAT_LABELS[cat]} escalation settings saved`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const saveMailbox = async () => {
    try {
      await api.put("/escalations/config-global", { inductions_mailbox: mailbox });
      toast.success("Inductions mailbox updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const runPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/escalations/run?dry_run=true");
      setPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };

  const runNow = async () => {
    if (!confirm("Send today's expiry reminder emails to all resolved recipients now?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/escalations/run?dry_run=false&force=true");
      toast.success(`Sent ${data.emails_sent ?? 0} email(s) covering ${data.items} expiring item(s)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Run failed"); }
    finally { setBusy(false); }
  };

  const searchEmps = async () => {
    try {
      const { data } = await api.get(`/escalations/employees?q=${encodeURIComponent(empQ)}`);
      setEmps(data.rows);
    } catch (e) { toast.error("Search failed"); }
  };

  const saveManager = async (emp) => {
    try {
      await api.patch("/escalations/employee-manager", { employee_id: emp.id, manager_email: emp.manager_email || null });
      toast.success(`${emp.name} manager ${emp.manager_email ? "set" : "cleared"}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  if (!cfg) {
    return <div className="flex items-center gap-2 text-sm text-slate-500 py-10 justify-center">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading escalation settings…</div>;
  }

  return (
    <div className="space-y-4" data-testid="escalations-panel">
      <div className="flex items-center gap-2">
        <BellRing className="w-4 h-4 text-gbe" />
        <span className="text-sm font-semibold text-slate-800">Expiry escalation emails</span>
        <span className="text-[11px] text-slate-500">Reminders ramp up per the ticked periods and go daily in the final week.</span>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={runPreview} disabled={busy}
                  data-testid="esc-preview">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Eye className="w-3.5 h-3.5 mr-1" />}
            Preview today's sends</Button>
          <Button size="sm" className="h-8 text-xs bg-gbe hover:bg-gbe-dark text-white" onClick={runNow} disabled={busy}
                  data-testid="esc-run-now"><Play className="w-3.5 h-3.5 mr-1" /> Run now</Button>
        </div>
      </div>

      {/* Category cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.keys(CAT_LABELS).map((cat) => {
          const c = cfg.categories[cat];
          return (
            <div key={cat} className={`rounded-lg border p-4 ${c.enabled ? "border-gbe/40 bg-white" : "border-slate-200 bg-slate-50/60"}`}
                 data-testid={`esc-card-${cat}`}>
              <div className="flex items-center justify-between mb-3">
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-800 cursor-pointer">
                  <input type="checkbox" checked={c.enabled} data-testid={`esc-enabled-${cat}`}
                         onChange={(e) => setCat(cat, { enabled: e.target.checked })} className="accent-[#003a5c]" />
                  {CAT_LABELS[cat]}
                  {!c.enabled && <Badge variant="outline" className="text-[9px] text-slate-400">off</Badge>}
                </label>
                <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => saveCat(cat)}
                        data-testid={`esc-save-${cat}`}><Save className="w-3 h-3 mr-1" />Save</Button>
              </div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Reminder periods before expiry</div>
              <div className="flex flex-wrap gap-3 mb-3 text-xs">
                {cfg.period_options.map((p) => (
                  <label key={p} className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={c.periods.includes(p)} className="accent-[#003a5c]"
                           data-testid={`esc-period-${cat}-${p}`}
                           onChange={(e) => setCat(cat, { periods: e.target.checked ? [...c.periods, p] : c.periods.filter((x) => x !== p) })} />
                    {cfg.period_labels[p]}
                  </label>
                ))}
                <label className="flex items-center gap-1.5 cursor-pointer font-medium text-rose-700">
                  <input type="checkbox" checked={c.daily_final} className="accent-[#dc2626]"
                         data-testid={`esc-daily-${cat}`}
                         onChange={(e) => setCat(cat, { daily_final: e.target.checked })} />
                  Daily (final week)
                </label>
              </div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Send to</div>
              <div className="flex flex-wrap gap-3 mb-2 text-xs">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={c.to_employee} className="accent-[#003a5c]"
                         data-testid={`esc-to-employee-${cat}`}
                         onChange={(e) => setCat(cat, { to_employee: e.target.checked })} />
                  {cat === "contracts" ? "Contract owner" : "Employee"}
                </label>
                {cat !== "contracts" && (
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={c.to_manager} className="accent-[#003a5c]"
                           data-testid={`esc-to-manager-${cat}`}
                           onChange={(e) => setCat(cat, { to_manager: e.target.checked })} />
                    Their manager
                  </label>
                )}
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={c.to_inductions_mailbox} className="accent-[#003a5c]"
                         data-testid={`esc-to-mailbox-${cat}`}
                         onChange={(e) => setCat(cat, { to_inductions_mailbox: e.target.checked })} />
                  {cfg.inductions_mailbox}
                </label>
              </div>
              <Input placeholder="Extra emails, comma-separated" className="h-7 text-[11px]"
                     data-testid={`esc-extra-${cat}`}
                     value={c.extra_raw !== undefined ? c.extra_raw : (c.extra_emails || []).join(", ")}
                     onChange={(e) => setCat(cat, { extra_raw: e.target.value })} />
            </div>
          );
        })}
      </div>

      {/* Global mailbox + manager tool + log */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-xs font-semibold text-slate-800 mb-2">Escalation mailbox</div>
          <div className="flex gap-2">
            <Input value={mailbox} onChange={(e) => setMailbox(e.target.value)} className="h-8 text-xs"
                   data-testid="esc-mailbox" />
            <Button size="sm" variant="outline" className="h-8" onClick={saveMailbox}
                    data-testid="esc-mailbox-save"><Save className="w-3.5 h-3.5" /></Button>
          </div>
          <div className="text-xs font-semibold text-slate-800 mt-4 mb-1">Recent runs</div>
          {log.length === 0 ? <div className="text-[11px] text-slate-400">No escalation runs yet.</div> : (
            <ul className="text-[11px] text-slate-600 space-y-1">
              {log.slice(0, 6).map((r, i) => (
                <li key={i}><span className="font-mono text-slate-400">{r.sent_at?.slice(0, 10)}</span> — {r.detail}</li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-4 lg:col-span-2">
          <div className="text-xs font-semibold text-slate-800 mb-2">Employee manager overrides
            <span className="font-normal text-slate-500 ml-1">(direct manager wins; otherwise the BU manager is used)</span></div>
          <div className="flex gap-2 mb-2">
            <Input placeholder="Search employee name or email…" value={empQ} className="h-8 text-xs"
                   data-testid="esc-emp-search"
                   onChange={(e) => setEmpQ(e.target.value)}
                   onKeyDown={(e) => e.key === "Enter" && searchEmps()} />
            <Button size="sm" variant="outline" className="h-8" onClick={searchEmps}
                    data-testid="esc-emp-search-btn"><Search className="w-3.5 h-3.5" /></Button>
          </div>
          {emps.length > 0 && (
            <div className="max-h-52 overflow-y-auto divide-y divide-slate-100">
              {emps.map((e, i) => (
                <div key={e.id} className="flex items-center gap-2 py-1.5 text-xs">
                  <span className="w-44 truncate text-slate-700">{e.name}</span>
                  <Input placeholder="manager@email" value={e.manager_email || ""}
                         data-testid={`esc-emp-mgr-${i}`}
                         onChange={(ev) => setEmps(emps.map((x) => x.id === e.id ? { ...x, manager_email: ev.target.value } : x))}
                         className="h-7 text-[11px] flex-1" />
                  <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => saveManager(e)}
                          data-testid={`esc-emp-mgr-save-${i}`}>Set</Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Preview results */}
      {preview && (
        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="esc-preview-panel">
          <div className="text-xs font-semibold text-slate-800 mb-2">
            Dry run — {preview.items} expiring item(s) → {preview.recipients} recipient(s)
            {preview.skipped_no_recipient > 0 && <span className="text-amber-600 ml-2">{preview.skipped_no_recipient} item(s) had no resolvable recipient</span>}
          </div>
          {(preview.preview || []).length === 0 ? (
            <div className="text-[11px] text-slate-400">Nothing would send today (check categories are enabled and periods match).</div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {preview.preview.map((r) => (
                <div key={r.email} className="text-xs">
                  <span className="font-semibold text-slate-700">{r.email}</span>
                  <span className="text-slate-400"> — {r.count} item(s):</span>
                  <span className="text-slate-600"> {r.items.slice(0, 4).map((i) => `${i.item} (${i.days_left}d)`).join(" · ")}{r.count > 4 ? " …" : ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
